import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { CustomersService } from '../../../services/customers.service';
import { ProductsService } from '../../../services/products.service';
import { VehiclesService } from '../../../services/vehicles.service';
import { Contract, Driver, VehicleSnapshot } from '../../../models/Contract';
import { Customer } from '../../../models/Customer';
import { Product } from '../../../models/Product';
import { Vehicle } from '../../../models/Vehicle';
import { ErrorService } from '../../../shared/services/error.service';
import { PaymentService } from '../../../services/payment.service';
import { AsaasCharge, AsaasChargeBatch, AsaasCustomer } from '../../../models/Payment';
import { AccountsReceivable, AccountsReceivableCreate, AccountsReceivableUpdate } from '../../../models/AccountsReceivable';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { CashflowService } from '../../../services/cashflow.service';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

interface AsaasChargeLink {
  externalReference: string;
  payId: string;
  billingLabel: string;
  bankSlipNumber?: string;
}

@Component({
  selector: 'app-contract-form',
  templateUrl: './contract-form.component.html',
  styleUrls: ['./contract-form.component.css']
})
export class ContractFormComponent implements OnInit, OnDestroy {
  contractForm: FormGroup;
  isEditMode = false;
  contractId: number | null = null;
  isLoading = false;
  isSaving = false;
  isGenerating = false;

  customers: Customer[] = [];
  vehicles: Vehicle[] = [];
  products: Product[] = [];
  templates: any[] = [];
  selectedTemplate: any = null;
  selectedTemplateVersion: any = null;
  selectedCustomer: Customer | null = null;
  selectedVehicle: Vehicle | null = null;
  selectedProduct: Product | null = null;
  contractWeeksCount = 4;
  contractWeeklyAmount = 0;

  // Steps do formulário (1: dados, 2: pagamentos)
  currentStep = 1;
  readonly totalSteps = 2;

  // Métodos de pagamento (para caução e 1ª parcela)
  paymentMethods = [
    { value: 'CASH', label: 'Dinheiro' },
    { value: 'PIX', label: 'Pix' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'CREDIT_CARD', label: 'Cartão (Asaas)' }
  ];

  depositPayments = [
    { method: 'CASH', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];

  firstInstallmentPayments = [
    { method: 'CASH', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];

  // Controle se o pagamento será em um ou dois métodos
  depositPaymentMode: 'single' | 'split' = 'single';
  firstInstallmentPaymentMode: 'single' | 'split' = 'single';

  // Parcelamento do boleto da caução (1 = à vista, até 10x)
  depositInstallments = 1;

  customerSearchResults: Customer[] = [];
  isSearchingCustomers = false;
  vehicleSearchResults: Vehicle[] = [];
  isSearchingVehicles = false;

  /** Cliente no Asaas (consultado pelo CPF/CNPJ ao selecionar cliente ou preencher documento) */
  asaasCustomerId: string | null = null;
  asaasCustomerLoading = false;
  asaasCustomerError: string | null = null;

  /** Dados completos do cliente Asaas (usado para preencher creditCardHolderInfo) */
  asaasCustomerInfo: AsaasCustomer | null = null;

  /** Dados do cartão para pagamento da caução via Asaas */
  // Cartão 1 (vinculado ao Método 1 da caução)
  depositCardInstallments = 1; // 1 a 10 parcelas para o método 1
  creditCardHolderName: string = '';
  creditCardNumber: string = '';
  creditCardExpiryMonth: string = '';
  creditCardExpiryYear: string = '';
  creditCardCcv: string = '';

  // Cartão 2 (vinculado ao Método 2 da caução)
  depositCardInstallments2 = 1; // 1 a 10 parcelas para o método 2
  creditCardHolderName2: string = '';
  creditCardNumber2: string = '';
  creditCardExpiryMonth2: string = '';
  creditCardExpiryYear2: string = '';
  creditCardCcv2: string = '';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private customersService: CustomersService,
    private productsService: ProductsService,
    private vehiclesService: VehiclesService,
    private paymentService: PaymentService,
    private accountsReceivableService: AccountsReceivableService,
    private cashflowService: CashflowService,
    private router: Router,
    private route: ActivatedRoute,
    private errorService: ErrorService
  ) {
    this.contractForm = this.createFormGroup();
  }

  private createFormGroup(): FormGroup {
    return this.fb.group({
      customerId: [1],
      vehicleId: [1],
      productId: [''],
      documentTemplateVersionId: [12],
      startDate: [''],
      endDate: [''],
      billingCycle: ['Semanal'],
      // Valores financeiros principais (carregados do produto)
      baseAmount: [0],        // valor da primeira parcela
      depositAmount: [0],     // valor do caução
      depositPaid: [false],
      depositPaidDate: [''],
      firstInstallmentPaid: [false],
      firstInstallmentPaidDate: [''],
      status: ['Pending'],
      driverName: [''],
      driverCpfCnpj: [''],
      driverCnhNumber: [''],
      driverCnhExpiryDate: [''],
      driverEmail: [''],
      driverPhone: [''],
      driverStreet: [''],
      driverNumber: [''],
      driverComplement: [''],
      driverDistrict: [''],
      driverCity: [''],
      driverState: [''],
      driverZipCode: [''],
      vehicleInspectionNotes: [''],
      vs_plate: [''],
      vs_model: [''],
      vs_year: [2020],
      vs_color: [''],
      vs_brand: [''],
      vs_renavam: [''],
      vs_chassis: [''],
      vs_currentKm: [null],
      placeholdersDataJson: ['']
    });
  }

  ngOnInit(): void {
    const today = this.getTodayDateString();
    this.contractForm.patchValue({ startDate: today });
    this.contractForm.get('startDate')?.disable({ emitEvent: false });

    this.setupFormValueChanges();
    this.setupSearchSubscriptions();
    this.loadInitialData();
    this.setupRouteSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFormValueChanges(): void {
    this.contractForm.get('productId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(productId => {
        if (productId) {
          this.loadProductDetails(productId);
        } else {
          this.selectedProduct = null;
          this.recalculateContractAmounts();
        }
      });

    this.contractForm.get('billingCycle')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculateContractAmounts());

    this.contractForm.get('startDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculateContractAmounts());

    this.contractForm.get('documentTemplateVersionId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(templateId => {
        if (templateId) this.loadTemplate(templateId);
      });

    this.contractForm.get('customerId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(customerId => {
        if (customerId) {
          this.selectedCustomer = this.customers.find(c => c.id === customerId) || null;
        }
      });

    this.contractForm.get('vehicleId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(vehicleId => {
        if (vehicleId) {
          this.selectedVehicle = this.vehicles.find(v => v.id === vehicleId) || null;
        }
      });

    this.contractForm.get('productId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(productId => {
        if (productId) {
          this.selectedProduct = this.products.find(p => p.id === productId) || null;
        }
      });

    this.contractForm.get('driverCpfCnpj')?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: string) => this.onDriverCpfCnpjChange(value));
  }

  private setupSearchSubscriptions(): void {
    this.contractForm.get('driverName')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((name: string) => this.handleCustomerSearch(name));

    this.contractForm.get('vs_plate')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((plate: string) => this.handleVehicleSearch(plate));
  }

  private handleCustomerSearch(name: string): void {
    const term = (name || '').trim();
    if (term.length < 3) {
      this.customerSearchResults = [];
      return;
    }
    this.isSearchingCustomers = true;
    this.customersService.getAllPaged({ page: 1, pageSize: 10, searchTerm: term })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.customerSearchResults = result.data || [];
          this.isSearchingCustomers = false;
        },
        error: () => { this.isSearchingCustomers = false; }
      });
  }

  private handleVehicleSearch(plate: string): void {
    const term = (plate || '').trim();
    if (term.length < 3) {
      this.vehicleSearchResults = [];
      return;
    }
    this.isSearchingVehicles = true;
    this.vehiclesService.getAllPaginated(1, 10, { search: term })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.vehicleSearchResults = result.items || [];
          this.isSearchingVehicles = false;
        },
        error: () => { this.isSearchingVehicles = false; }
      });
  }

  private setupRouteSubscription(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('id');
        if (id) {
          this.contractId = +id;
          this.isEditMode = true;
          this.loadInitialData().then(() => this.contractId && this.loadContract(this.contractId));
        } else {
          this.contractId = null;
          this.isEditMode = false;
          this.contractForm.patchValue({ depositPaid: false, firstInstallmentPaid: false }, { emitEvent: false });
        }
      });
  }

  loadInitialData(): Promise<void> {
    this.isLoading = true;
    return Promise.all([
      this.loadProducts(),
      this.loadTemplateVersions()
    ]).then(() => {
      this.isLoading = false;
    }).catch(() => {
      this.isLoading = false;
    });
  }

  private loadProducts(): Promise<void> {
    return new Promise(resolve => {
      this.productsService.getProducts()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (products: Product[]) => {
            this.products = products.filter(p => p.isActive);
            const productId = this.contractForm.get('productId')?.value;
            if (productId) {
              this.selectedProduct = this.products.find(p => p.id === productId) || null;
            }
            this.recalculateContractAmounts();
            resolve();
          },
          error: (err) => {
            this.errorService.showErrorMessage('Erro ao carregar produtos: ' + err.message);
            resolve();
          }
        });
    });
  }

  private loadTemplateVersions(): Promise<void> {
    return new Promise(resolve => {
      this.contractsService.getTemplateVersions(2)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (versions) => {
            const syntheticTemplate = { id: 2, name: 'Contrato de Locação de Veículo', area: 'Comercial', versions };
            this.templates = [syntheticTemplate];
            this.selectedTemplate = syntheticTemplate;
            const version12 = versions.find((v: any) => v.id === 12) || versions.find((v: any) => v.version === 12);
            this.selectedTemplateVersion = version12 || versions[0] || null;
            if (this.selectedTemplateVersion) {
              this.contractForm.patchValue({ documentTemplateVersionId: this.selectedTemplateVersion.id });
            }
            resolve();
          },
          error: (err) => {
            this.errorService.showErrorMessage('Erro ao carregar template: ' + err.message);
            resolve();
          }
        });
    });
  }

  loadContract(id: number): void {
    this.isLoading = true;
    this.contractsService.getContractComplete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contractData) => {
          this.populateFormWithContract(contractData);
          if (contractData.contract.documentTemplateVersionId) {
            this.loadTemplate(contractData.contract.documentTemplateVersionId);
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.errorService.showErrorMessage('Erro ao carregar contrato: ' + err.message);
          this.isLoading = false;
          this.router.navigate(['/contracts']);
        }
      });
  }

  private populateFormWithContract(contractData: any): void {
    const contract = contractData.contract;
    const driver = contract.driver || {};
    const address = driver.address || {};

    this.contractForm.patchValue({
      customerId: contract.customerId,
      vehicleId: contract.vehicleId,
      productId: contract.productId,
      documentTemplateVersionId: contract.documentTemplateVersionId,
      startDate: contract.startDate,
      endDate: contract.endDate,
      billingCycle: contract.billingCycle,
      baseAmount: contract.baseAmount,
      depositAmount: contract.depositAmount,
      depositPaid: contract.depositPaid === true,
      depositPaidDate: contract.depositPaidDate ?? '',
      firstInstallmentPaid: contract.firstInstallmentPaid === true,
      firstInstallmentPaidDate: contract.firstInstallmentPaidDate ?? '',
      status: contract.status,
      driverName: driver.name || '',
      driverCpfCnpj: driver.cpfCnpj || '',
      driverCnhNumber: driver.cnhNumber || '',
      driverCnhExpiryDate: driver.cnhExpiryDate || '',
      driverEmail: driver.email || '',
      driverPhone: driver.phone || '',
      driverStreet: address.street || '',
      driverNumber: address.number || '',
      driverComplement: address.complement || '',
      driverDistrict: address.district || '',
      driverCity: address.city || '',
      driverState: address.state || '',
      driverZipCode: address.zipCode || '',
      vehicleInspectionNotes: contractData.vehicleSnapshot?.inspectionNotes || ''
    });

    this.selectedCustomer = contractData.customer || this.customers.find(c => c.id === contract.customerId) || null;
    this.selectedVehicle = contractData.vehicle || this.vehicles.find(v => v.id === contract.vehicleId) || null;
    this.selectedProduct = contractData.product || this.products.find(p => p.id === contract.productId) || null;

    setTimeout(() => {
      ['customerId', 'vehicleId', 'productId', 'documentTemplateVersionId'].forEach(field => {
        this.contractForm.get(field)?.updateValueAndValidity({ emitEvent: false });
      });
    }, 100);
  }

  searchCustomers(): void {
    const term = (this.contractForm.get('driverName')?.value || '').trim();
    if (!term || term.length < 2) {
      this.errorService.showInfoMessage('Digite pelo menos 2 caracteres para pesquisar o cliente.');
      return;
    }
    this.isSearchingCustomers = true;
    this.customerSearchResults = [];
    this.customersService.getAllPaged({ page: 1, pageSize: 50, searchTerm: term })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.customerSearchResults = result.data || [];
          this.isSearchingCustomers = false;
          if (this.customerSearchResults.length === 0) {
            this.errorService.showInfoMessage('Nenhum cliente encontrado.');
          }
        },
        error: (err) => {
          this.errorService.showErrorMessage('Erro ao buscar clientes: ' + (err?.message || err));
          this.isSearchingCustomers = false;
        }
      });
  }

  onSelectCustomer(customer: Customer): void {
    this.isSearchingCustomers = true;
    this.customersService.getById(customer.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (full) => {
          this.selectedCustomer = full;
          this.contractForm.patchValue({
            customerId: full.id,
            driverName: full.name || full.contactPersonName || '',
            driverCpfCnpj: full.documentNumber || '',
            driverCnhNumber: full.driverLicenseNumber || '',
            driverCnhExpiryDate: full.driverLicenseExpiryDate ? this.formatDateForInput(full.driverLicenseExpiryDate) : '',
            driverEmail: full.email || full.contactPersonEmail || '',
            driverPhone: full.primaryPhone || full.contactPersonPhone || '',
            driverStreet: full.street || '',
            driverNumber: full.number || '',
            driverComplement: full.complement || '',
            driverDistrict: full.neighborhood || '',
            driverCity: full.city || '',
            driverState: full.state || '',
            driverZipCode: full.zipCode || ''
          }, { emitEvent: false });
          this.customerSearchResults = [];
          this.isSearchingCustomers = false;
          this.errorService.showInfoMessage(`Cliente "${full.name}" selecionado. Dados do motorista preenchidos.`);
          this.loadAsaasCustomerByCpf(full.documentNumber || '');
        },
        error: (err) => {
          this.errorService.showErrorMessage('Erro ao carregar cliente: ' + (err?.message || err));
          this.isSearchingCustomers = false;
        }
      });
  }

  clearCustomerSelection(): void {
    this.selectedCustomer = null;
    this.contractForm.patchValue({ customerId: null }, { emitEvent: false });
    this.customerSearchResults = [];
    this.asaasCustomerId = null;
    this.asaasCustomerError = null;
    this.asaasCustomerInfo = null;
  }

  private onDriverCpfCnpjChange(value: string): void {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length === 11 || digits.length === 14) {
      this.loadAsaasCustomerByCpf(digits);
    } else {
      this.asaasCustomerId = null;
      this.asaasCustomerError = null;
    }
  }

  loadAsaasCustomerByCpf(cpfCnpj: string): void {
    const digits = (cpfCnpj || '').replace(/\D/g, '');
    if (!digits || (digits.length !== 11 && digits.length !== 14)) {
      this.asaasCustomerId = null;
      this.asaasCustomerError = null;
      return;
    }
    this.asaasCustomerLoading = true;
    this.asaasCustomerError = null;
    this.paymentService.listAsaasCustomers({ cpfCnpj: digits })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.asaasCustomerLoading = false;
          const first = res?.data?.[0];
          this.asaasCustomerId = first?.id ?? null;
          this.asaasCustomerInfo = first || null;
          if (!this.asaasCustomerId && res?.data && (res.data as any[]).length === 0) {
            this.asaasCustomerError = 'Cliente não encontrado no Asaas para este CPF/CNPJ.';
          }
        },
        error: (err) => {
          this.asaasCustomerLoading = false;
          this.asaasCustomerId = null;
          this.asaasCustomerError = err?.message || 'Erro ao consultar cliente Asaas.';
          this.asaasCustomerInfo = null;
        }
      });
  }

  /** Indica se há algum método de pagamento da caução configurado como BOLETO */
  hasDepositBoleto(): boolean {
    return this.depositPayments?.some(p => p.method === 'BOLETO') ?? false;
  }

  /** Indica se há algum método de pagamento da caução configurado como CARTÃO (Asaas) */
  hasDepositCard(): boolean {
    return this.depositPayments?.some(p => p.method === 'CREDIT_CARD') ?? false;
  }

  searchVehicles(): void {
    const plate = (this.contractForm.get('vs_plate')?.value || '').trim();
    const model = (this.contractForm.get('vs_model')?.value || '').trim();
    const term = plate || model;
    this.isSearchingVehicles = true;
    this.vehicleSearchResults = [];
    this.vehiclesService.getAllPaginated(1, 50, { search: term || undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.vehicleSearchResults = result.items || [];
          this.isSearchingVehicles = false;
          if (term && this.vehicleSearchResults.length === 0) {
            this.errorService.showInfoMessage('Nenhum veículo encontrado.');
          }
        },
        error: (err) => {
          this.errorService.showErrorMessage('Erro ao buscar veículos: ' + (err?.message || err));
          this.isSearchingVehicles = false;
        }
      });
  }

  onSelectVehicle(vehicle: Vehicle): void {
    this.selectedVehicle = vehicle;
    this.contractForm.patchValue({
      vehicleId: vehicle.id,
      vs_plate: vehicle.plateNumber || '',
      vs_model: vehicle.model || '',
      vs_year: vehicle.modelYear || vehicle.manufactureYear || new Date().getFullYear(),
      vs_color: vehicle.color || '',
      vs_brand: vehicle.brand || '',
      vs_renavam: vehicle.renavam || '',
      vs_chassis: vehicle.chassisNumber || '',
      vs_currentKm: vehicle.currentMileage ?? null
    }, { emitEvent: false });
    this.vehicleSearchResults = [];
    this.errorService.showInfoMessage(`Veículo ${vehicle.plateNumber} selecionado. Dados preenchidos.`);
  }

  clearVehicleSelection(): void {
    this.selectedVehicle = null;
    this.contractForm.patchValue({ vehicleId: null }, { emitEvent: false });
    this.vehicleSearchResults = [];
  }

  onTemplateChange(): void {
    if (this.selectedTemplate) {
      this.selectedTemplateVersion = null;
      this.contractForm.patchValue({ documentTemplateVersionId: null });
    }
  }

  onTemplateVersionChange(): void {
    if (this.selectedTemplateVersion) {
      this.contractForm.patchValue({ documentTemplateVersionId: this.selectedTemplateVersion.id });
    }
  }

  loadTemplate(templateId: number): void {
    this.contractsService.getLatestTemplateVersion(templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (template) => {
          this.selectedTemplate = template;
        },
        error: (err) => {
          this.errorService.showErrorMessage('Erro ao carregar template: ' + err.message);
        }
      });
  }

  onSubmit(): void {
    this.isEditMode ? this.updateContract() : this.generateContract();
  }

  goToStep(step: number): void {
    this.currentStep = step;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  generateContract(): void {
    const formValue = this.contractForm.getRawValue();
    const customerId = this.selectedCustomer?.id ?? formValue.customerId;
    const vehicleId = this.selectedVehicle?.id ?? formValue.vehicleId;

    if (!this.validatePaymentSplits()) {
      return;
    }

    if (!customerId || !vehicleId) {
      this.errorService.showErrorMessage('Selecione um cliente e um veículo antes de gerar o contrato.');
      return;
    }

    this.isGenerating = true;

    const driverData = this.buildDriver(formValue);
    
    const contractData = {
      contract: {
        productId: Number(formValue.productId), // Ensure productId is a number
        customerId: Number(customerId),
        vehicleId: Number(vehicleId),
        documentTemplateVersionId: 12,
        startDate: this.formatDateToISO(new Date()),
        endDate: this.formatDateToISO(this.addDaysToDate(new Date(), Number(this.selectedProduct?.days) || 30)),
        billingCycle: String(formValue.billingCycle || 'Semanal'), // Ensure billingCycle is a string
        // Indicação de pagamento não é controlada aqui na criação:
        // sempre começa como não pago; datas serão controladas pelo fluxo financeiro.
        depositPaid: false,
        depositPaidDate: null,
        firstInstallmentPaid: false,
        firstInstallmentPaidDate: null,
        placeholdersDataJson: this.buildPlaceholdersJson(formValue),
        status: 'Pending' // sempre inicia como pendente de assinatura
      },
      driver: {
        ...driverData,
        complement: driverData.complement || ''
      },
      vehicleSnapshot: this.buildVehicleSnapshot(formValue)
    };

   this.contractsService.createContract(contractData).subscribe({
      next: (response) => {
        this.errorService.showInfoMessage(response.message || 'Contrato criado com sucesso!');

        const contractId = response?.contract?.id;
        const payments$ = contractId
          ? this.setupContractFinancials(response.contract, formValue)
          : of(undefined);

        payments$.subscribe({
          next: () => {
            this.isGenerating = false;
            this.router.navigate(['/contracts', contractId, 'view']);
          },
          error: () => {
            this.isGenerating = false;
            this.router.navigate(['/contracts', contractId, 'view']);
          }
        });
      },
      error: (err) => {
        this.errorService.showErrorMessage('Erro ao criar contrato: ' + err.message);
        this.isGenerating = false;
      }
    });
  }

  updateContract(): void {
    this.isSaving = true;
    const formValue = this.contractForm.value;

    if (!this.validatePaymentSplits()) {
      this.isSaving = false;
      return;
    }

    if (!this.selectedCustomer || !this.selectedVehicle || !this.selectedProduct) {
      this.errorService.showErrorMessage('Selecione cliente, veículo e produto antes de atualizar o contrato.');
      this.isSaving = false;
      return;
    }

    const contractData: Omit<Contract, 'createdAt' | 'updatedAt'> = {
      id: this.contractId!,
      contractNumber: this.contractId!.toString().padStart(4, '0'),
      customerId: formValue.customerId,
      vehicleId: formValue.vehicleId,
      productId: formValue.productId,
      documentTemplateVersionId: formValue.documentTemplateVersionId,
      startDate: formValue.startDate,
      endDate: formValue.endDate,
      billingCycle: formValue.billingCycle,
      baseAmount: formValue.baseAmount,
      depositAmount: formValue.depositAmount,
      depositPaid: formValue.depositPaid,
      depositPaidDate: formValue.depositPaidDate,
      firstInstallmentPaid: formValue.firstInstallmentPaid,
      firstInstallmentPaidDate: formValue.firstInstallmentPaidDate,
      status: formValue.status,
      placeholdersDataJson: JSON.stringify({
        cliente_nome: this.selectedCustomer.name,
        veiculo_modelo: this.selectedVehicle.model,
        veiculo_placa: this.selectedVehicle.plateNumber,
        produto_nome: this.selectedProduct.name,
        valor_base: formValue.baseAmount,
        valor_caucao: formValue.depositAmount
      }),
      driver: this.buildDriver(formValue),
      vehicleSnapshot: this.buildVehicleSnapshot(formValue),
      createdBy: 'system'
    };

    if (this.contractId) {
      this.contractsService.updateContract(this.contractId, contractData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.errorService.showInfoMessage('Contrato atualizado com sucesso!');
            this.router.navigate(['/contracts']);
          },
          error: (err) => {
            this.errorService.showErrorMessage('Erro ao atualizar contrato: ' + err.message);
            this.isSaving = false;
          }
        });
    }
  }

  private buildDriver(formValue: any): {
    name: string;
    cpfCnpj: string;
    cnhNumber: string;
    cnhExpiryDate: string;
    email: string;
    phone: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    zipCode: string;
  } {
    return {
        name: formValue.driverName || '',
        cpfCnpj: formValue.driverCpfCnpj || '',
        cnhNumber: formValue.driverCnhNumber || '',
        cnhExpiryDate: formValue.driverCnhExpiryDate || '',
        email: formValue.driverEmail || '',
        phone: formValue.driverPhone || '',
        street: formValue.driverStreet || '',
        number: formValue.driverNumber || '',
        complement: formValue.driverComplement ?? '', // <-- aqui está o ponto!
        district: formValue.driverDistrict || '',
        city: formValue.driverCity || '',
        state: formValue.driverState || '',
        zipCode: formValue.driverZipCode || ''
    };
  }

  private buildVehicleSnapshot(formValue: any): VehicleSnapshot {
    return {
      plate: formValue.vs_plate,
      model: formValue.vs_model,
      year: Number(formValue.vs_year),
      color: formValue.vs_color,
      brand: formValue.vs_brand,
      renavam: formValue.vs_renavam,
      chassis: formValue.vs_chassis,
      currentKm: Number(formValue.vs_currentKm),
      inspectionNotes: formValue.vehicleInspectionNotes
    };
  }

  private buildPlaceholdersJson(formValue: any): string {
    if (formValue.placeholdersDataJson?.trim()) {
      return formValue.placeholdersDataJson;
    }
    return JSON.stringify({
      cliente_nome: formValue.driverName,
      veiculo_modelo: formValue.vs_model,
      valor_mensal: this.formatPrice(this.contractWeeklyAmount * this.contractWeeksCount),
      valor_semanal: this.formatPrice(this.contractWeeklyAmount),
      pagamento: {
        asaasCustomerId: this.asaasCustomerId || undefined,
        caucaoTotal: formValue.depositAmount,
        caucaoSplit: this.depositPayments
          .filter(p => p.amount && p.amount > 0)
          .map(p => ({ metodo: p.method, valor: p.amount })),
        primeiraParcelaTotal: formValue.baseAmount,
        primeiraParcelaSplit: this.firstInstallmentPayments
          .filter(p => p.amount && p.amount > 0)
          .map(p => ({ metodo: p.method, valor: p.amount }))
      }
    });
  }

  private loadProductDetails(productId: number): void {
    this.productsService.getProduct(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (product) => {
          this.selectedProduct = product;
          this.recalculateContractAmounts();
        },
        error: (err) => {
          this.errorService.showErrorMessage('Erro ao carregar produto: ' + err.message);
        }
      });
  }

  private recalculateContractAmounts(): void {
    if (!this.selectedProduct) {
      this.contractWeeklyAmount = 0;
      this.contractWeeksCount = 4;
      // Zera valores financeiros quando não há produto selecionado
      this.contractForm.patchValue(
        { baseAmount: 0, depositAmount: 0 },
        { emitEvent: false }
      );
      this.resetPaymentSplits();
      return;
    }

    const days = Number(this.selectedProduct.days) || 0;
    let parcelas = Math.floor(days / 7);
    if (parcelas < 1) parcelas = 1;

    this.contractWeeksCount = parcelas;
    const base = Number(this.selectedProduct.basePrice) || 0;
    this.contractWeeklyAmount = parcelas > 0 ? (parcelas * base) / parcelas : base;

    // Ao selecionar o produto, já preenche o valor da 1ª parcela e do caução
    this.contractForm.patchValue(
      {
        baseAmount: base,
        depositAmount: Number(this.selectedProduct.depositAmount) || 0
      },
      { emitEvent: false }
    );
    this.initPaymentSplits(base, Number(this.selectedProduct.depositAmount) || 0);

    const raw = this.contractForm.getRawValue();
    const startStr = raw.startDate || this.getTodayDateString();
    const computedEnd = this.addDaysToDateString(startStr, days);
    
    this.contractForm.get('endDate')?.disable({ emitEvent: false });
    this.contractForm.patchValue({ endDate: computedEnd }, { emitEvent: false });
  }

  private resetPaymentSplits(): void {
    this.depositPayments = [
      { method: 'CASH', amount: 0 },
      { method: 'PIX', amount: 0 }
    ];
    this.firstInstallmentPayments = [
      { method: 'CASH', amount: 0 },
      { method: 'PIX', amount: 0 }
    ];

    this.depositPaymentMode = 'single';
    this.firstInstallmentPaymentMode = 'single';
  }

  private getDepositTotal(): number {
    return Number(this.contractForm.get('depositAmount')?.value) || 0;
  }

  private getFirstInstallmentTotal(): number {
    return Number(this.contractForm.get('baseAmount')?.value) || 0;
  }

  /**
   * Valida se os splits de pagamento (caução e 1ª parcela) não excedem
   * os respectivos valores totais configurados no formulário.
   */
  private validatePaymentSplits(): boolean {
    const depositTotal = this.getDepositTotal();
    const depositSplitSum = this.depositPayments
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    if (depositTotal >= 0 && depositSplitSum > depositTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da caução não pode ultrapassar o valor total da caução.');
      return false;
    }

    const firstInstallmentTotal = this.getFirstInstallmentTotal();
    const firstInstallmentSplitSum = this.firstInstallmentPayments
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    if (firstInstallmentTotal >= 0 && firstInstallmentSplitSum > firstInstallmentTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da 1ª parcela não pode ultrapassar o valor total da 1ª parcela.');
      return false;
    }

    return true;
  }

  setDepositPaymentMode(mode: 'single' | 'split'): void {
    this.depositPaymentMode = mode;
    this.recalculateDepositSplit();
  }

  setFirstInstallmentPaymentMode(mode: 'single' | 'split'): void {
    this.firstInstallmentPaymentMode = mode;
    this.recalculateFirstInstallmentSplit();
  }

  private recalculateDepositSplit(): void {
    const total = this.getDepositTotal();
    if (total <= 0) {
      this.depositPayments[0].amount = 0;
      this.depositPayments[1].amount = 0;
      return;
    }

    if (this.depositPaymentMode === 'single') {
      // 100% no primeiro método
      this.depositPayments[0].amount = total;
      this.depositPayments[1].amount = 0;
    } else {
      // Split: se já existir um valor válido no primeiro, ajusta o segundo.
      let first = Number(this.depositPayments[0].amount) || 0;
      if (first <= 0 || first >= total) {
        first = Math.round((total / 2) * 100) / 100;
      }
      const second = total - first;
      this.depositPayments[0].amount = first;
      this.depositPayments[1].amount = second;
    }
  }

  private recalculateFirstInstallmentSplit(): void {
    const total = this.getFirstInstallmentTotal();
    if (total <= 0) {
      this.firstInstallmentPayments[0].amount = 0;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }

    if (this.firstInstallmentPaymentMode === 'single') {
      this.firstInstallmentPayments[0].amount = total;
      this.firstInstallmentPayments[1].amount = 0;
    } else {
      let first = Number(this.firstInstallmentPayments[0].amount) || 0;
      if (first <= 0 || first >= total) {
        first = Math.round((total / 2) * 100) / 100;
      }
      const second = total - first;
      this.firstInstallmentPayments[0].amount = first;
      this.firstInstallmentPayments[1].amount = second;
    }
  }

  onDepositAmountChange(index: number, value: number): void {
    const total = this.getDepositTotal();
    if (total <= 0) {
      this.depositPayments[0].amount = 0;
      this.depositPayments[1].amount = 0;
      return;
    }

    const v = Math.max(0, Number(value) || 0);

    if (this.depositPaymentMode === 'single') {
      // Apenas um método: trava no intervalo [0, total] sem criar segundo método
      this.depositPayments[0].amount = Math.min(v, total);
      this.depositPayments[1].amount = 0;
      return;
    }

    if (index === 0) {
      const first = Math.min(v, total);
      this.depositPayments[0].amount = first;
      this.depositPayments[1].amount = total - first;
    } else {
      const second = Math.min(v, total);
      this.depositPayments[1].amount = second;
      this.depositPayments[0].amount = total - second;
    }
  }

  onFirstInstallmentAmountChange(index: number, value: number): void {
    const total = this.getFirstInstallmentTotal();
    if (total <= 0) {
      this.firstInstallmentPayments[0].amount = 0;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }

    const v = Math.max(0, Number(value) || 0);

    if (this.firstInstallmentPaymentMode === 'single') {
      // Apenas um método: trava no intervalo [0, total] sem criar segundo método
      this.firstInstallmentPayments[0].amount = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }

    if (index === 0) {
      const first = Math.min(v, total);
      this.firstInstallmentPayments[0].amount = first;
      this.firstInstallmentPayments[1].amount = total - first;
    } else {
      const second = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = second;
      this.firstInstallmentPayments[0].amount = total - second;
    }
  }

  onDepositMethodChange(index: number, method: string): void {
    // atualiza o método selecionado
    if (!this.depositPayments[index]) {
      return;
    }
    this.depositPayments[index].method = method;

    // se escolher cartão, força modo split (2 métodos) para permitir dividir a caução
    if (method === 'CREDIT_CARD') {
      this.depositPaymentMode = 'split';
      this.recalculateDepositSplit();
    }
  }

  private initPaymentSplits(firstInstallmentTotal: number, depositTotal: number): void {
    // Por padrão, joga 100% no primeiro método (modo "single")
    this.depositPayments = [
      { method: this.depositPayments[0]?.method || 'CASH', amount: depositTotal },
      { method: this.depositPayments[1]?.method || 'PIX', amount: 0 }
    ];
    this.firstInstallmentPayments = [
      { method: this.firstInstallmentPayments[0]?.method || 'CASH', amount: firstInstallmentTotal },
      { method: this.firstInstallmentPayments[1]?.method || 'PIX', amount: 0 }
    ];
  }

  onCancel(): void {
    this.router.navigate(['/contracts']);
  }

  private getTodayDateString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private addDaysToDateString(dateStr: string, daysToAdd: number): string {
    const base = new Date(dateStr);
    if (isNaN(base.getTime())) return dateStr;
    
    const d = new Date(base.getTime());
    d.setDate(d.getDate() + daysToAdd);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private addDaysToDate(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private formatDateToISO(date: Date): string {
    return date.toISOString().split('T')[0] + 'T00:00:00Z';
  }

  /** Retorna data no formato YYYY-MM-DD para input type="date" (ex.: da API do cliente) */
  private formatDateForInput(value: string | Date | null | undefined): string {
    if (value == null || value === '') return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  /**
   * Cadastro financeiro automático ao gerar contrato: títulos em Contas a Receber + cobranças Asaas vinculadas (pay_...).
   */
  private setupContractFinancials(createdContract: any, formValue: any): Observable<void> {
    const asaasCustomerId = this.asaasCustomerId;
    const today = this.getTodayDateString();
    const dueDateBase = formValue.startDate || today;
    const asaasObservables: Observable<AsaasChargeLink | AsaasChargeLink[] | null>[] = [];
    const arBatch: AccountsReceivableCreate[] = [];

    const customerIdForAr =
      Number(createdContract?.customerId ?? formValue.customerId ?? this.selectedCustomer?.id ?? 0) || undefined;
    const contractNumberForAr =
      createdContract?.contractNumber?.toString?.() ?? createdContract?.id?.toString?.() ?? '';
    const saleOrderKey = `CONTRATO-${createdContract.id}`;

    if (!customerIdForAr) {
      this.errorService.showErrorMessage('Cliente não identificado — títulos financeiros não foram gerados.');
      return of(undefined);
    }

    const onError = (msg: string) => (err: any) => {
      this.errorService.showErrorMessage(msg + (err?.error?.message || err?.message || 'Erro desconhecido'));
      return of(null);
    };

    const pushAsaas = (obs: Observable<unknown>, externalReference: string, billingType: string) => {
      asaasObservables.push(
        obs.pipe(
          map((res) => this.extractChargeLink(res, externalReference, billingType)),
          catchError(onError('Erro em cobrança Asaas: '))
        )
      );
    };

    const pushAsaasBatch = (obs: Observable<unknown>, batch: AsaasChargeBatch[], errorMsg: string) => {
      asaasObservables.push(
        obs.pipe(
          map((res) => {
            const links = this.extractChargeLinksFromBatch(res, batch);
            return links.length ? links : null;
          }),
          catchError(onError(errorMsg))
        )
      );
    };

    const addAccountsReceivable = (options: {
      value: number;
      dueDate: string;
      description: string;
      externalReference?: string;
      installmentNumber?: number;
      totalInstallments?: number;
      status?: string;
      amountReceived?: number;
      receiptMethod?: string;
    }) => {
      const docNumber = options.externalReference || `CTR-${createdContract.id}-${options.installmentNumber || 1}`;
      arBatch.push({
        contractNumber: contractNumberForAr || undefined,
        documentNumber: docNumber,
        documentType: 'CTR',
        issueDate: today,
        dueDate: options.dueDate,
        originalAmount: Number(options.value),
        amountToReceive: Number(options.value),
        status: options.status || 'Pendente',
        amountReceived: options.amountReceived,
        category: 'Venda',
        customerId: customerIdForAr,
        costCenterId: 1,
        description: options.description,
        receiptMethod: options.receiptMethod,
        saleOrderNumber: saleOrderKey,
        installmentNumber: options.installmentNumber,
        totalInstallments: options.totalInstallments
      });
    };

    // 0) Dinheiro / cartão na 1ª parcela — só CR (sem Asaas)
    this.firstInstallmentPayments
      .filter((p) => p.amount && p.amount > 0 && (p.method === 'CASH' || p.method === 'CREDIT_CARD'))
      .forEach((p, index) => {
        const value = Number(p.amount);
        const externalReference = `CONTRATO-${createdContract.id}-P1-${p.method}-${index + 1}`;
        addAccountsReceivable({
          value,
          dueDate: dueDateBase,
          description: `1ª Parcela - ${p.method}`,
          externalReference,
          installmentNumber: 1,
          totalInstallments: this.contractWeeksCount || 1,
          status: 'Recebido',
          amountReceived: value,
          receiptMethod: p.method === 'CASH' ? 'Dinheiro' : 'Cartão'
        });
      });

    // 0.1) Caução em dinheiro
    this.depositPayments
      .filter((p) => p.amount && p.amount > 0 && p.method === 'CASH')
      .forEach((p, index) => {
        const value = Number(p.amount);
        addAccountsReceivable({
          value,
          dueDate: dueDateBase,
          description: 'Caução - Dinheiro',
          externalReference: `CONTRATO-${createdContract.id}-CAUCAO-CASH-${index + 1}`,
          status: 'Recebido',
          amountReceived: value,
          receiptMethod: 'Dinheiro'
        });
      });

    if (!asaasCustomerId) {
      if (arBatch.length === 0) return of(undefined);
      return this.accountsReceivableService.createAccountsReceivableBatch(arBatch).pipe(
        tap(() => this.cashflowService.requestRefresh()),
        map(() => undefined),
        catchError((err) => {
          onError('Erro ao criar contas a receber: ')(err);
          return of(undefined);
        })
      );
    }

    const customerId = asaasCustomerId;

    // 1) 1ª parcela: PIX ou Boleto 1x
    this.firstInstallmentPayments
      .filter(p => p.amount && p.amount > 0 && (p.method === 'PIX' || p.method === 'BOLETO'))
      .forEach(p => {
        const charge: AsaasCharge = {
          customerId,
          billingType: p.method as 'PIX' | 'BOLETO',
          value: Number(p.amount),
          dueDate: dueDateBase,
          description: `1ª Parcela - ${p.method}`,
          externalReference: `CONTRATO-${createdContract.id}-P1-${p.method}`
        };
        addAccountsReceivable({
          value: charge.value,
          dueDate: charge.dueDate,
          description: charge.description || '1ª Parcela',
          externalReference: charge.externalReference,
          installmentNumber: 1,
          totalInstallments: this.contractWeeksCount || 1
        });
        pushAsaas(
          this.paymentService.createAsaasCharge(charge),
          charge.externalReference || '',
          p.method
        );
      });

    // 2) Caução em PIX
    this.depositPayments
      .filter(p => p.amount && p.amount > 0 && p.method === 'PIX')
      .forEach((p, index) => {
        const charge: AsaasCharge = {
          customerId,
          billingType: 'PIX',
          value: Number(p.amount),
          dueDate: dueDateBase,
          description: `Caução - PIX`,
          externalReference: `CONTRATO-${createdContract.id}-CAUCAO-PIX-${index + 1}`
        };
        addAccountsReceivable({
          value: charge.value,
          dueDate: charge.dueDate,
          description: charge.description || 'Caução - PIX',
          externalReference: charge.externalReference
        });
        pushAsaas(
          this.paymentService.createAsaasCharge(charge),
          charge.externalReference || '',
          'PIX'
        );
      });

    // 3) Caução em CARTÃO (Asaas) - pode haver até 2 métodos de cartão
    const cardDeposits = this.depositPayments
      .filter(p => p.amount && p.amount > 0 && p.method === 'CREDIT_CARD');

    if (cardDeposits.length > 0) {
      if (!this.asaasCustomerInfo) {
        this.errorService.showErrorMessage('Não foi possível carregar os dados do cliente Asaas para pagamento com cartão.');
      } else if (!this.creditCardNumber || !this.creditCardExpiryMonth || !this.creditCardExpiryYear || !this.creditCardCcv) {
        this.errorService.showErrorMessage('Preencha todos os dados do cartão para realizar o pagamento da caução.');
      } else {
        const customerInfo = this.asaasCustomerInfo as AsaasCustomer;

        cardDeposits.forEach((p, index) => {
          const value = Number(p.amount);
          if (!value || value <= 0) {
            return;
          }

          const useSecondCard = cardDeposits.length > 1 && index === 1;

          const holderName = useSecondCard
            ? (this.creditCardHolderName2 || customerInfo.name)
            : (this.creditCardHolderName || customerInfo.name);

          const cardNumber = useSecondCard ? this.creditCardNumber2 : this.creditCardNumber;
          const expiryMonth = useSecondCard ? this.creditCardExpiryMonth2 : this.creditCardExpiryMonth;
          const expiryYear = useSecondCard ? this.creditCardExpiryYear2 : this.creditCardExpiryYear;
          const cvv = useSecondCard ? this.creditCardCcv2 : this.creditCardCcv;

          const installmentsForThisCard = Math.max(
            1,
            Math.min(10, useSecondCard ? (this.depositCardInstallments2 || 1) : (this.depositCardInstallments || 1))
          );

          const charge: AsaasCharge = {
            customerId,
            billingType: 'CREDIT_CARD',
            value,
            dueDate: dueDateBase,
            description: `Caução - Cartão (${installmentsForThisCard}x) - parte ${index + 1}/${cardDeposits.length}`,
            externalReference: `CONTRATO-${createdContract.id}-CAUCAO-CARTAO-${index + 1}`,
            installmentCount: installmentsForThisCard,
            creditCard: {
              holderName,
              number: cardNumber,
              expiryMonth,
              expiryYear,
              ccv: cvv
            },
            creditCardHolderInfo: {
              name: customerInfo.name,
              email: customerInfo.email || formValue.driverEmail,
              cpfCnpj: customerInfo.cpfCnpj,
              postalCode: customerInfo.postalCode || formValue.driverZipCode,
              addressNumber: customerInfo.addressNumber || formValue.driverNumber,
              addressComplement: customerInfo.addressComplement || formValue.driverComplement,
              phone: customerInfo.phone || formValue.driverPhone,
              mobilePhone: customerInfo.mobilePhone || formValue.driverPhone
            }
          };

          addAccountsReceivable({
            value: charge.value,
            dueDate: charge.dueDate,
            description: charge.description || 'Caução - Cartão',
            externalReference: charge.externalReference
          });

          pushAsaas(
            this.paymentService.createAsaasCharge(charge),
            charge.externalReference || '',
            'CREDIT_CARD'
          );
        });
      }
    }

    // 4) Caução em BOLETO
    const boletoTotal = this.depositPayments
      .filter(p => p.amount && p.amount > 0 && p.method === 'BOLETO')
      .reduce((sum, p) => sum + Number(p.amount), 0);
    const installments = Math.max(1, Math.min(10, this.depositInstallments || 1));

    if (boletoTotal > 0 && installments === 1) {
      const charge: AsaasCharge = {
        customerId,
        billingType: 'BOLETO',
        value: boletoTotal,
        dueDate: dueDateBase,
        description: 'Caução - Boleto à vista',
        externalReference: `CONTRATO-${createdContract.id}-CAUCAO-1`
      };
      addAccountsReceivable({
        value: charge.value,
        dueDate: charge.dueDate,
        description: charge.description || 'Caução - Boleto à vista',
        externalReference: charge.externalReference
      });
      pushAsaas(
        this.paymentService.createAsaasCharge(charge),
        charge.externalReference || '',
        'BOLETO'
      );
    } else if (boletoTotal > 0 && installments > 1) {
      const totalCents = Math.round(boletoTotal * 100);
      const baseCents = Math.floor(totalCents / installments);
      const depositBatch: AsaasChargeBatch[] = [];
      for (let i = 0; i < installments; i++) {
        const isLast = i === installments - 1;
        const cents = isLast ? totalCents - baseCents * i : baseCents;
        depositBatch.push({
          customerId,
          value: cents / 100,
          dueDate: dueDateBase,
          description: `Caução - parcela ${i + 1}/${installments}`,
          externalReference: `CONTRATO-${createdContract.id}-CAUCAO-${i + 1}`
        });
      }
      depositBatch.forEach((item, index) => {
        addAccountsReceivable({
          value: item.value,
          dueDate: item.dueDate,
          description: item.description || `Caução - parcela ${index + 1}/${installments}`,
          externalReference: item.externalReference,
          installmentNumber: index + 1,
          totalInstallments: installments
        });
      });
      pushAsaasBatch(
        this.paymentService.createAsaasChargeBatch(depositBatch),
        depositBatch,
        'Erro ao gerar boletos da caução: '
      );
    }

    // 5) Plano (aluguel)
    const totalInstallments = this.contractWeeksCount || 0;
    if (totalInstallments > 0 && formValue.baseAmount && customerId) {
      const planBatch: AsaasChargeBatch[] = [];
      const parcelaValue = Number(formValue.baseAmount);
      const startDate = formValue.startDate || today;
      for (let i = 0; i < totalInstallments; i++) {
        const dueDate = this.addDaysToDateString(startDate, i * 7);
        planBatch.push({
          customerId,
          value: parcelaValue,
          dueDate,
          description: `Aluguel - parcela ${i + 1}/${totalInstallments} (${formValue.billingCycle || 'Semanal'})`,
          externalReference: `CONTRATO-${createdContract.id}-PLANO-${i + 1}`
        });
      }
      planBatch.forEach((item, index) => {
        addAccountsReceivable({
          value: item.value,
          dueDate: item.dueDate,
          description: item.description || `Aluguel - parcela ${index + 1}/${totalInstallments}`,
          externalReference: item.externalReference,
          installmentNumber: index + 1,
          totalInstallments: totalInstallments
        });
      });
      pushAsaasBatch(
        this.paymentService.createAsaasChargeBatch(planBatch),
        planBatch,
        'Erro ao gerar boletos do plano: '
      );
    }

    if (arBatch.length === 0) {
      return of(undefined);
    }

    return this.accountsReceivableService.createAccountsReceivableBatch(arBatch).pipe(
      switchMap(() => (asaasObservables.length ? forkJoin(asaasObservables) : of([]))),
      switchMap((links) => {
        const flat: AsaasChargeLink[] = [];
        for (const item of links as Array<AsaasChargeLink | AsaasChargeLink[] | null>) {
          if (!item) continue;
          if (Array.isArray(item)) flat.push(...item);
          else flat.push(item);
        }
        return this.linkAsaasChargesToTitles(saleOrderKey, flat);
      }),
      tap(() => this.cashflowService.requestRefresh()),
      map(() => undefined),
      catchError((err) => {
        onError('Erro ao registrar financeiro do contrato: ')(err);
        return of(undefined);
      }),
      takeUntil(this.destroy$)
    );
  }

  private extractChargeLink(
    res: unknown,
    externalReference: string,
    billingType: string
  ): AsaasChargeLink | null {
    const d = (res || {}) as Record<string, unknown>;
    const payId = String(d['id'] ?? d['Id'] ?? '').trim();
    const ref = String(d['externalReference'] ?? d['ExternalReference'] ?? externalReference ?? '').trim();
    if (!payId || !ref) return null;
    const label =
      billingType === 'PIX' ? 'PIX (Asaas)' : billingType === 'BOLETO' ? 'Boleto (Asaas)' : 'Asaas';
    const nosso = (d['nossoNumero'] ?? d['NossoNumero']) as string | undefined;
    return { externalReference: ref, payId, billingLabel: label, bankSlipNumber: nosso };
  }

  private extractChargeLinksFromBatch(res: unknown, batch: AsaasChargeBatch[]): AsaasChargeLink[] {
    const raw = Array.isArray(res)
      ? res
      : (res as Record<string, unknown>)?.['items'] ??
        (res as Record<string, unknown>)?.['Items'] ??
        (res as Record<string, unknown>)?.['payments'] ??
        [];
    const arr = Array.isArray(raw) ? raw : [];
    return arr
      .map((item, i) => this.extractChargeLink(item, batch[i]?.externalReference || '', 'BOLETO'))
      .filter((x): x is AsaasChargeLink => !!x);
  }

  private linkAsaasChargesToTitles(saleOrderKey: string, links: AsaasChargeLink[]): Observable<void> {
    if (!links.length) return of(undefined);
    return this.accountsReceivableService.getBySaleOrderNumber(saleOrderKey, { page: 1, pageSize: 300 }).pipe(
      switchMap((res) => {
        const titles = res.items || [];
        const updates: Observable<AccountsReceivable>[] = [];
        for (const link of links) {
          const title = titles.find((t) => (t.documentNumber || '').trim() === link.externalReference.trim());
          if (!title?.id || !link.payId) continue;
          updates.push(
            this.accountsReceivableService.updateAccountsReceivable(this.buildArAsaasLinkUpdate(title, link))
          );
        }
        return updates.length ? forkJoin(updates) : of([]);
      }),
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  private buildArAsaasLinkUpdate(ar: AccountsReceivable, link: AsaasChargeLink): AccountsReceivableUpdate {
    return {
      id: ar.id,
      documentNumber: ar.documentNumber,
      documentType: ar.documentType,
      issueDate: this.formatDateForApi(ar.issueDate),
      dueDate: this.formatDateForApi(ar.dueDate),
      originalAmount: ar.originalAmount,
      discountAmount: ar.discountAmount,
      interestAmount: ar.interestAmount,
      fineAmount: ar.fineAmount,
      amountToReceive: ar.amountToReceive,
      amountReceived: ar.amountReceived ?? 0,
      status: ar.status,
      category: ar.category,
      receiptMethod: link.billingLabel,
      receiptReference: link.payId,
      customerId: ar.customerId,
      costCenterId: ar.costCenterId,
      description: ar.description,
      saleOrderNumber: ar.saleOrderNumber,
      bankSlipNumber: link.bankSlipNumber ?? ar.bankSlipNumber,
      checkNumber: ar.checkNumber
    };
  }

  private formatDateForApi(date: Date | string | undefined): string {
    if (!date) return '';
    if (typeof date === 'string') return date.includes('T') ? date.split('T')[0] : date.substring(0, 10);
    return date.toISOString().split('T')[0];
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }

  get submitButtonText(): string {
    if (this.isGenerating) return 'Gerando...';
    if (this.isSaving) return 'Salvando...';
    return this.isEditMode ? 'Salvar' : 'Gerar Contrato';
  }

  get isSubmitDisabled(): boolean {
    return this.contractForm.invalid || this.isLoading || this.isSaving || this.isGenerating;
  }

  // ======== Helpers de cartão (UI) ========

  isCardBack = false;
  isCardBack2 = false;

  get cardBrand(): string {
    const digits = (this.creditCardNumber || '').replace(/\D/g, '');
    if (!digits) return 'Cartão';
    if (/^4[0-9]{0,}$/.test(digits)) return 'Visa';
    if (/^5[1-5][0-9]{0,}$/.test(digits)) return 'Mastercard';
    if (/^3[47][0-9]{0,}$/.test(digits)) return 'American Express';
    if (/^6(?:011|5)/.test(digits)) return 'Discover';
    return 'Cartão';
  }

  get maskedCardNumber(): string {
    const digits = (this.creditCardNumber || '').replace(/\D/g, '').slice(0, 16).padEnd(16, '•');
    const groups = [
      digits.slice(0, 4),
      digits.slice(4, 8),
      digits.slice(8, 12),
      digits.slice(12, 16)
    ];
    return groups.join(' ').trim();
  }

  onCreditCardNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let digits = (input.value || '').replace(/\D/g, '');
    // limita a 16 dígitos
    digits = digits.slice(0, 16);
    const groups = digits.match(/.{1,4}/g) || [];
    const formatted = groups.join(' ');
    this.creditCardNumber = formatted;
  }

  get cardBrand2(): string {
    const digits = (this.creditCardNumber2 || '').replace(/\D/g, '');
    if (!digits) return 'Cartão';
    if (/^4[0-9]{0,}$/.test(digits)) return 'Visa';
    if (/^5[1-5][0-9]{0,}$/.test(digits)) return 'Mastercard';
    if (/^3[47][0-9]{0,}$/.test(digits)) return 'American Express';
    if (/^6(?:011|5)/.test(digits)) return 'Discover';
    return 'Cartão';
  }

  get maskedCardNumber2(): string {
    const digits = (this.creditCardNumber2 || '').replace(/\D/g, '').slice(0, 16).padEnd(16, '•');
    const groups = [
      digits.slice(0, 4),
      digits.slice(4, 8),
      digits.slice(8, 12),
      digits.slice(12, 16)
    ];
    return groups.join(' ').trim();
  }

  onCreditCardNumber2Input(event: Event): void {
    const input = event.target as HTMLInputElement;
    let digits = (input.value || '').replace(/\D/g, '');
    digits = digits.slice(0, 16);
    const groups = digits.match(/.{1,4}/g) || [];
    const formatted = groups.join(' ');
    this.creditCardNumber2 = formatted;
  }

  setCardBack(isBack: boolean, cardIndex: 1 | 2 = 1): void {
    if (cardIndex === 1) {
      this.isCardBack = isBack;
    } else {
      this.isCardBack2 = isBack;
    }
  }
}
